import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  X, 
  CheckCircle2, 
  ShieldCheck, 
  Mail, 
  Phone, 
  Globe2, 
  Briefcase, 
  Languages, 
  Calendar,
  Layers,
  Sparkles,
  BookOpen
} from 'lucide-react';
import { StaffMember, StaffDepartment, UserRole } from '@ftt/shared';
import { STAFF_DEPARTMENTS_CONFIG, INDIAN_LANGUAGES_LIST } from '@ftt/shared';

export const AdminStaffPage: React.FC = () => {
  const [selectedDept, setSelectedDept] = useState<string>('ALL');
  const [selectedRole, setSelectedRole] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState<StaffDepartment>('EDITORIAL');
  const [role, setRole] = useState<UserRole>('EDITOR');
  const [bio, setBio] = useState('');
  const [assignedLanguages, setAssignedLanguages] = useState<string[]>(['English']);
  const [assignedProjectCount, setAssignedProjectCount] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [joinedAt, setJoinedAt] = useState('');

  const queryClient = useQueryClient();

  const { data: staffRes, isLoading } = useQuery({
    queryKey: ['admin-staff', { dept: selectedDept, role: selectedRole, search: searchQuery }],
    queryFn: () => {
      let url = '/admin/staff';
      const params = new URLSearchParams();
      if (selectedDept !== 'ALL') params.append('department', selectedDept);
      if (selectedRole !== 'ALL') params.append('role', selectedRole);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const staffList: StaffMember[] = staffRes || [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        email,
        phone: phone || null,
        jobTitle,
        department,
        role,
        bio: bio || null,
        assignedLanguages,
        assignedProjectCount,
        isActive,
        joinedAt: joinedAt || new Date().toISOString(),
      };

      if (editingStaff?.id) {
        return api.put(`/admin/staff/${editingStaff.id}`, payload);
      } else {
        return api.post('/admin/staff', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-staff'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/staff/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-staff'] });
    },
  });

  const resetForm = () => {
    setEditingStaff(null);
    setName('');
    setEmail('');
    setPhone('');
    setJobTitle('');
    setDepartment('EDITORIAL');
    setRole('EDITOR');
    setBio('');
    setAssignedLanguages(['English']);
    setAssignedProjectCount(0);
    setIsActive(true);
    setJoinedAt(new Date().toISOString().split('T')[0]);
  };

  const handleEdit = (staff: StaffMember) => {
    setEditingStaff(staff);
    setName(staff.name);
    setEmail(staff.email);
    setPhone(staff.phone || '');
    setJobTitle(staff.jobTitle);
    setDepartment(staff.department);
    setRole(staff.role);
    setBio(staff.bio || '');
    setAssignedLanguages(staff.assignedLanguages || ['English']);
    setAssignedProjectCount(staff.assignedProjectCount || 0);
    setIsActive(staff.isActive);
    setJoinedAt(staff.joinedAt ? staff.joinedAt.split('T')[0] : '');
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const toggleLanguage = (lang: string) => {
    if (assignedLanguages.includes(lang)) {
      setAssignedLanguages(assignedLanguages.filter((l) => l !== lang));
    } else {
      setAssignedLanguages([...assignedLanguages, lang]);
    }
  };

  // Summary Metrics
  const totalStaff = staffList.length;
  const activeCount = staffList.filter((s) => s.isActive).length;
  const translationStaffCount = staffList.filter((s) => s.department === 'TRANSLATION').length;
  const allLanguages = Array.from(new Set(staffList.flatMap((s) => s.assignedLanguages || [])));

  return (
    <div className="space-y-8 font-sans">
      
      {/* Header & Primary Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-400 font-mono uppercase tracking-widest font-bold">
              ORGANIZATION &amp; TALENT
            </span>
            <span className="text-slate-600 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-400 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white flex items-center gap-2.5">
            <Users className="w-7 h-7 text-brand-400" />
            <span>Internal Staff &amp; Role Directory</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Manage editorial leads, theological translators, production directors, and operational staff permissions.
          </p>
        </div>

        <button
          onClick={handleCreate}
          className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2 shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Add Staff Member</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-mono uppercase tracking-wider">Total Team Members</span>
            <Users className="w-4 h-4 text-brand-400" />
          </div>
          <div className="text-2xl font-bold font-serif text-white">{totalStaff}</div>
          <div className="text-[11px] text-emerald-400 font-mono">{activeCount} Currently Active</div>
        </div>

        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-mono uppercase tracking-wider">Translation Department</span>
            <Languages className="w-4 h-4 text-purple-400" />
          </div>
          <div className="text-2xl font-bold font-serif text-white">{translationStaffCount}</div>
          <div className="text-[11px] text-purple-400 font-mono">Vernacular Linguists</div>
        </div>

        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-mono uppercase tracking-wider">Languages Covered</span>
            <Globe2 className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-bold font-serif text-white">{allLanguages.length}</div>
          <div className="text-[11px] text-slate-400 font-mono">Indian Vernaculars</div>
        </div>

        <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-mono uppercase tracking-wider">Access Roles</span>
            <ShieldCheck className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-bold font-serif text-white">3 Tiers</div>
          <div className="text-[11px] text-amber-400 font-mono">Super Admin / Admin / Editor</div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by name, email, role, language..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-brand-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Department Filter */}
          <select
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white font-medium focus:outline-hidden"
          >
            <option value="ALL">All Departments</option>
            {Object.entries(STAFF_DEPARTMENTS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>

          {/* Role Filter */}
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white font-medium focus:outline-hidden"
          >
            <option value="ALL">All Roles</option>
            <option value="SUPER_ADMIN">Super Admin</option>
            <option value="ADMIN">Admin</option>
            <option value="EDITOR">Editor</option>
          </select>
        </div>
      </div>

      {/* Staff Members Grid & Table */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading staff records...</div>
      ) : staffList.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-950 border border-slate-800 text-center space-y-3">
          <Users className="w-8 h-8 text-slate-600 mx-auto" />
          <div className="text-sm font-bold text-white">No Staff Members Found</div>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            Try adjusting your search criteria or add a new staff member to the internal directory.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {staffList.map((staff) => {
            const deptLabel = STAFF_DEPARTMENTS_CONFIG[staff.department]?.label || staff.department;
            const deptColors: Record<StaffDepartment, string> = {
              MANAGEMENT: 'bg-amber-950 text-amber-300 border border-amber-800/60',
              EDITORIAL: 'bg-blue-950 text-blue-300 border border-blue-800/60',
              TRANSLATION: 'bg-purple-950 text-purple-300 border border-purple-800/60',
              PRODUCTION_DESIGN: 'bg-indigo-950 text-indigo-300 border border-indigo-800/60',
              FINANCE_OPERATIONS: 'bg-emerald-950 text-emerald-300 border border-emerald-800/60',
              PARTNER_RELATIONS: 'bg-rose-950 text-rose-300 border border-rose-800/60',
            };
            const deptColor = deptColors[staff.department] || 'bg-slate-800 text-slate-300';

            return (
              <div
                key={staff.id}
                className="p-6 rounded-2xl bg-slate-950 border border-slate-800 hover:border-slate-700 transition-all flex flex-col justify-between space-y-4 relative group"
              >
                <div className="space-y-3">
                  {/* Top Bar: Dept Badge & Status */}
                  <div className="flex items-center justify-between">
                    <span className={`px-2.5 py-1 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider ${deptColor}`}>
                      {deptLabel}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${staff.isActive ? 'bg-emerald-500' : 'bg-slate-500'}`} />
                      <span className="text-[10px] font-mono uppercase text-slate-400">
                        {staff.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Profile Info */}
                  <div className="flex items-start gap-3 pt-1">
                    <div className="w-12 h-12 rounded-full bg-brand-900 border border-brand-600/50 flex items-center justify-center text-brand-300 font-bold text-base shrink-0">
                      {staff.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="overflow-hidden">
                      <h3 className="font-serif font-bold text-base text-white truncate">
                        {staff.name}
                      </h3>
                      <div className="text-xs text-brand-400 font-medium truncate">
                        {staff.jobTitle}
                      </div>
                      <div className="flex items-center gap-2 pt-1 text-[10px] font-mono">
                        <span className={`px-2 py-0.5 rounded-sm font-semibold ${
                          staff.role === 'SUPER_ADMIN' ? 'bg-amber-950 text-amber-300 border border-amber-800/60' :
                          staff.role === 'ADMIN' ? 'bg-blue-950 text-blue-300 border border-blue-800/60' :
                          'bg-slate-800 text-slate-300 border border-slate-700'
                        }`}>
                          {staff.role}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  {staff.bio && (
                    <p className="text-xs text-slate-300 leading-relaxed font-serif italic line-clamp-2">
                      &ldquo;{staff.bio}&rdquo;
                    </p>
                  )}

                  {/* Languages Covered */}
                  {staff.assignedLanguages && staff.assignedLanguages.length > 0 && (
                    <div className="space-y-1.5 pt-2 border-t border-slate-900">
                      <span className="text-[10px] font-mono uppercase text-slate-500 block">Languages &amp; Vernaculars</span>
                      <div className="flex flex-wrap gap-1">
                        {staff.assignedLanguages.map((lang) => (
                          <span
                            key={lang}
                            className="px-2 py-0.5 rounded-md bg-slate-900 text-slate-300 text-[10px] font-mono border border-slate-800"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="space-y-1 text-xs text-slate-400 pt-2 border-t border-slate-900 font-mono">
                    <div className="flex items-center gap-2 truncate">
                      <Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <span className="truncate">{staff.email}</span>
                    </div>
                    {staff.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span>{staff.phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-3 border-t border-slate-900 flex items-center justify-between text-xs">
                  <span className="text-[10px] text-slate-500 font-mono">
                    Projects: <strong className="text-white">{staff.assignedProjectCount || 0}</strong>
                  </span>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(staff)}
                      className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
                      title="Edit Staff Member"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Remove staff member ${staff.name}?`)) {
                          deleteMutation.mutate(staff.id);
                        }
                      }}
                      className="p-1.5 rounded-lg bg-slate-900 hover:bg-rose-950 text-slate-400 hover:text-rose-300 transition-colors"
                      title="Delete Staff Member"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-in fade-in">
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-slate-950 border border-slate-800 p-6 shadow-2xl text-white space-y-6">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <span className="text-[10px] text-brand-400 font-mono uppercase tracking-widest font-bold">
                  {editingStaff ? 'EDIT STAFF RECORD' : 'NEW STAFF REGISTRATION'}
                </span>
                <h3 className="text-lg font-serif font-bold text-white">
                  {editingStaff ? editingStaff.name : 'Add Internal Team Member'}
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg bg-slate-900 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              
              <div className="sm:col-span-2 space-y-1">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold">Full Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Jonathan David"
                  className="w-full p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white focus:border-brand-500 focus:outline-hidden"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold">Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jonathan@forthetruth.in"
                  className="w-full p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white focus:border-brand-500 focus:outline-hidden"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold">Phone Number</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white focus:border-brand-500 focus:outline-hidden"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold">Job Title *</label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Senior Translation Editor"
                  className="w-full p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white focus:border-brand-500 focus:outline-hidden"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold">Department *</label>
                <select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value as StaffDepartment)}
                  className="w-full p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white focus:border-brand-500 focus:outline-hidden font-medium"
                >
                  {Object.entries(STAFF_DEPARTMENTS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold">System Access Role *</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                  className="w-full p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white focus:border-brand-500 focus:outline-hidden font-medium"
                >
                  <option value="SUPER_ADMIN">SUPER_ADMIN (Full Platform Authority)</option>
                  <option value="ADMIN">ADMIN (Operations &amp; Editorial Management)</option>
                  <option value="EDITOR">EDITOR (Manuscripts &amp; Translation Workflows)</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold">Joined Date</label>
                <input
                  type="date"
                  value={joinedAt}
                  onChange={(e) => setJoinedAt(e.target.value)}
                  className="w-full p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white focus:border-brand-500 focus:outline-hidden"
                />
              </div>

              {/* Vernacular Language Checkbox Selector */}
              <div className="sm:col-span-2 space-y-2 pt-2 border-t border-slate-800">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold block">
                  Vernacular Language Competencies
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {INDIAN_LANGUAGES_LIST.map((lang) => {
                    const isSelected = assignedLanguages.includes(lang);
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleLanguage(lang)}
                        className={`p-2 rounded-lg text-left text-xs font-mono transition-all flex items-center justify-between border ${
                          isSelected
                            ? 'bg-brand-900/60 border-brand-500 text-brand-200 font-bold'
                            : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                        }`}
                      >
                        <span>{lang}</span>
                        {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="sm:col-span-2 space-y-1 pt-2 border-t border-slate-800">
                <label className="text-[10px] font-mono uppercase text-slate-400 font-semibold">Short Biography / Expertise</label>
                <textarea
                  rows={2}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Specialist in Hindi theological literature and Pauline epistles..."
                  className="w-full p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white focus:border-brand-500 focus:outline-hidden"
                />
              </div>

              <div className="sm:col-span-2 flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-slate-800">
                <div>
                  <span className="text-xs font-bold text-white block">Active Status</span>
                  <span className="text-[11px] text-slate-400">Allows internal staff login and project assignment</span>
                </div>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 accent-brand-500 rounded cursor-pointer"
                />
              </div>

            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => saveMutation.mutate()}
                disabled={!name || !email || !jobTitle || saveMutation.isPending}
                className="px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white text-xs font-bold shadow-md transition-all flex items-center gap-2"
              >
                {saveMutation.isPending ? 'Saving...' : editingStaff ? 'Update Staff Member' : 'Register Staff Member'}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};
